import React, { useState, useEffect, useCallback } from 'react';
import './App.css';
import ScannerMode from './components/ScannerMode';
import TabletMode from './components/TabletMode';
import ManualMode from './components/ManualMode';
import BadgePreview from './components/BadgePreview';
import ConfirmDialog from './components/ConfirmDialog';
import LabelPreview from './components/LabelPreview';
import DfpLogo from './components/DfpLogo';
import { api } from './services/api';
import { storage } from './services/storage';

function App() {
    const [mode, setMode] = useState('scanner'); // 'scanner', 'tablet', 'manual'
    const [selectedEvent, setSelectedEvent] = useState('');
    const [dataSource, setDataSource] = useState('bc'); // 'bc' or 'confrenzy'
    const [events, setEvents] = useState([]);
    const [currentRegistration, setCurrentRegistration] = useState(null);
    const [autoPrintMode, setAutoPrintMode] = useState(false);
    const [message, setMessage] = useState(null);
    const [showConfirm, setShowConfirm] = useState(false);
    const [pendingRegistration, setPendingRegistration] = useState(null);
    const [showEventSelector, setShowEventSelector] = useState(false);
    const [pendingRegistrations, setPendingRegistrations] = useState([]);
    const [lastManualSearch, setLastManualSearch] = useState({ term: '', results: [] });
    const [showLabelPreview, setShowLabelPreview] = useState(false);
    const [showTabletUI, setShowTabletUI] = useState(true);
    const [eventProgress, setEventProgress] = useState(null);

    const loadEvents = useCallback(async () => {
        try {
            const eventList = await api.getEvents(dataSource);
            setEvents(eventList);
            
            // Restore previously selected event from localStorage
            const savedEventId = storage.getSelectedEvent();
            if (savedEventId && eventList.some(e => e.id === savedEventId)) {
                setSelectedEvent(savedEventId);
            }
        } catch (error) {
            console.error('Failed to load events:', error);
            setMessage({ type: 'error', text: 'Failed to load events: ' + error.message });
        }
    }, [dataSource]);

    // Load events on mount
    useEffect(() => {
        loadEvents();
    }, [loadEvents]);

    // Load dataSource from sessionStorage
    useEffect(() => {
        const savedSource = sessionStorage.getItem('badgeScanner_dataSource');
        if (savedSource && (savedSource === 'bc' || savedSource === 'confrenzy')) {
            setDataSource(savedSource);
        }
    }, []);

    // Reload events when dataSource changes
    useEffect(() => {
        loadEvents();
    }, [loadEvents]);

    // Poll for event progress updates every 10 seconds
    useEffect(() => {
        if (!selectedEvent) {
            return;
        }

        // Don't poll if selected event doesn't exist in current event list
        if (!events.some(e => e.id === selectedEvent)) {
            console.log(`[App] Event ${selectedEvent} not found in current event list, skipping progress polling`);
            return;
        }

        // Fetch immediately on event selection
        const fetchProgress = async () => {
            try {
                const progress = await api.getEventProgress(selectedEvent, dataSource);
                setEventProgress(progress);
            } catch (error) {
                console.error('Failed to fetch event progress:', error);
                // Continue polling even if one fetch fails
            }
        };

        fetchProgress();

        // Set up polling every 10 seconds
        const interval = setInterval(fetchProgress, 10000);

        // Cleanup: clear interval when component unmounts or event changes
        return () => {
            clearInterval(interval);
        };
    }, [selectedEvent, dataSource, events]);

    const handleEventChange = (eventId) => {
        setSelectedEvent(eventId);
        storage.saveSelectedEvent(eventId);
        setCurrentRegistration(null);
        setAutoPrintMode(false);
        setMessage(null);
        setEventProgress(null); // Reset progress while loading
        // Polling will start via useEffect when selectedEvent changes
    };

    const handleDataSourceChange = (newSource) => {
        setSelectedEvent(''); // Reset event selection when changing source
        setEventProgress(null); // Clear progress immediately
        setDataSource(newSource);
        sessionStorage.setItem('badgeScanner_dataSource', newSource);
        setCurrentRegistration(null);
        setAutoPrintMode(false);
        setMessage(null);
    };

    const handleRegistrationFound = useCallback((registration) => {
        setMessage(null);
        
        // Check if already printed (using BC data from badgePrinted field)
        const printed = registration.badgePrinted > 0;
        
        if (printed) {
            // Already printed - show confirmation dialog
            setPendingRegistration(registration);
            setShowConfirm(true);
        } else {
            // Not printed yet
            if (mode === 'scanner' || mode === 'tablet') {
                // For scanner/tablet modes: auto-print without preview
                setAutoPrintMode(true);
                setCurrentRegistration(registration);
            } else {
                // For manual mode: show preview first
                setCurrentRegistration(registration);
            }
        }
    }, [mode]);

    const handleScanResult = useCallback(async (qrCode) => {
        if (!selectedEvent) {
            setMessage({ type: 'error', text: 'Please select an event first' });
            return;
        }

        setMessage({ type: 'info', text: 'Looking up registration...' });

        try {
            const result = await api.searchByQR(qrCode, dataSource);
            
            // Filter results to only include the selected event
            let filteredResults = [];
            
            if (Array.isArray(result)) {
                filteredResults = result.filter(reg => reg.eventId === selectedEvent);
            } else if (result.multiple && result.registrations && result.registrations.length > 0) {
                filteredResults = result.registrations.filter(reg => reg.eventId === selectedEvent);
            } else if (result.eventId === selectedEvent) {
                filteredResults = [result];
            }
            
            if (filteredResults.length === 0) {
                setMessage({ type: 'error', text: 'No registrations found for this event' });
            } else if (filteredResults.length === 1) {
                handleRegistrationFound(filteredResults[0]);
            } else {
                // Multiple registrations for selected event
                setPendingRegistrations(filteredResults);
                setShowEventSelector(true);
                setMessage(null);
            }
        } catch (error) {
            setMessage({ type: 'error', text: error.message || 'Registration not found' });
        }
    }, [selectedEvent, dataSource, handleRegistrationFound]);

    const handleConfirmPrint = () => {
        setShowConfirm(false);
        setCurrentRegistration(pendingRegistration);
        setPendingRegistration(null);
    };

    const handleCancelPrint = () => {
        setShowConfirm(false);
        setPendingRegistration(null);
        setMessage({ type: 'info', text: 'Print cancelled' });
    };

    const handlePrintComplete = (registration) => {
        const count = storage.markAsPrinted(registration.qrCode, registration.name);
        const successMessage = `✅ Badge printed for ${registration.name}` + (count > 1 ? ` (${count}x today)` : '');
        
        setMessage({ 
            type: 'success', 
            text: successMessage
        });

        // Fetch fresh progress from server (polling will update it in next cycle)
        // For immediate UI feedback, we'll let polling handle the update

        if (autoPrintMode) {
            // For scanner/tablet modes: wait 2 seconds then resume scanning
            setTimeout(() => {
                setCurrentRegistration(null);
                setAutoPrintMode(false);
                setMessage(null);
            }, 2000);
        } else {
            // For manual mode: clear after 3 seconds
            setCurrentRegistration(null);
            setTimeout(() => setMessage(null), 3000);
        }
    };

    const handleCancel = () => {
        setCurrentRegistration(null);
        setAutoPrintMode(false);
        setMessage(null);
        // Manual mode will restore search when remounted
    };

    const handleSaveSearchState = useCallback((searchTerm, results) => {
        setLastManualSearch({ term: searchTerm, results });
    }, []);

    const handleSelectRegistrationFromList = (registration) => {
        handleRegistrationFound(registration);
        setShowEventSelector(false);
        setPendingRegistrations([]);
    };

    const handleModeChange = (newMode) => {
        setMode(newMode);
        setCurrentRegistration(null);
        setAutoPrintMode(false);
        setMessage(null);
    };

    return (
        <div className="App">
            <header className={`header ${mode === 'tablet' && !showTabletUI ? 'hidden' : ''}`}>
                <div className="header-content">
                    <div 
                        className="header-brand"
                        onClick={() => window.location.href = '/'}
                        style={{ cursor: 'pointer' }}
                        title="Go to home"
                    >
                        <DfpLogo className="header-logo" />
                        <span className="header-divider" aria-hidden="true"></span>
                        <h1>Badge Scanner</h1>
                    </div>
                    <div className="header-controls">
                        <select
                            id="data-source-select"
                            value={dataSource}
                            onChange={(e) => handleDataSourceChange(e.target.value)}
                            className="data-source-dropdown event-dropdown"
                            title="Switch between Business Central and Confrenzy"
                        >
                            <option value="bc">Business Central (BC)</option>
                            <option value="confrenzy">Confrenzy</option>
                        </select>
                        <button 
                            className="label-preview-btn"
                            onClick={() => setShowLabelPreview(!showLabelPreview)}
                            title="Toggle Label Preview (Debug)"
                            style={{ display: 'none' }}
                        >
                            📐
                        </button>
                    </div>
                </div>
            </header>

            <main className={`main-content ${mode === 'tablet' ? 'tablet-fullscreen' : ''}`}>
                {!currentRegistration && (
                    <>
                        <div className={`event-selector-container ${mode === 'tablet' && !showTabletUI ? 'hidden' : ''}`}>
                            <label htmlFor="event-select">Select Event:</label>
                            <select
                                id="event-select"
                                value={selectedEvent}
                                onChange={(e) => handleEventChange(e.target.value)}
                                className="event-dropdown"
                            >
                                <option value="">-- Choose an event --</option>
                                {events.map(event => (
                                    <option key={event.id} value={event.id}>
                                        {event.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {eventProgress && selectedEvent && (
                            <div className={`progress-bar-container ${mode === 'tablet' && !showTabletUI ? 'hidden' : ''}`}>
                                <div className="progress-info">
                                    <span className="progress-label">Scanned:</span>
                                    <span className="progress-stats">{eventProgress.scanned} / {eventProgress.total}</span>
                                </div>
                                <div className="progress-bar">
                                    <div 
                                        className="progress-fill" 
                                        style={{ width: eventProgress.total > 0 ? `${(eventProgress.scanned / eventProgress.total) * 100}%` : '0%' }}
                                    ></div>
                                </div>
                                <div className="progress-percentage">
                                    {eventProgress.total > 0 ? `${Math.round((eventProgress.scanned / eventProgress.total) * 100)}%` : '0%'}
                                </div>
                            </div>
                        )}

                        {mode !== 'tablet' && (
                            <div className="mode-selector">
                                <button 
                                    className={`mode-btn ${mode === 'scanner' ? 'active' : ''}`}
                                    onClick={() => handleModeChange('scanner')}
                                >
                                    Scanner
                                </button>
                                <button 
                                    className={`mode-btn ${mode === 'tablet' ? 'active' : ''}`}
                                    onClick={() => handleModeChange('tablet')}
                                >
                                    Tablet
                                </button>
                                <button 
                                    className={`mode-btn ${mode === 'manual' ? 'active' : ''}`}
                                    onClick={() => handleModeChange('manual')}
                                >
                                    Manual
                                </button>
                            </div>
                        )}

                        <div className={`scanner-container ${mode === 'tablet' ? 'fullscreen' : ''}`}>
                            {mode === 'tablet' && (
                                <button 
                                    className="tablet-ui-toggle"
                                    onClick={() => setShowTabletUI(!showTabletUI)}
                                    title={showTabletUI ? 'Hide controls' : 'Show controls'}
                                >
                                    {showTabletUI ? '⌃' : '⌄'}
                                </button>
                            )}
                            {mode === 'scanner' && (
                                <ScannerMode 
                                    onScanResult={handleScanResult}
                                    eventId={selectedEvent}
                                />
                            )}
                            {mode === 'tablet' && (
                                <TabletMode 
                                    onScanResult={handleScanResult}
                                    eventId={selectedEvent}
                                />
                            )}
                            {mode === 'manual' && (
                                <ManualMode 
                                    onSelectRegistration={handleRegistrationFound}
                                    eventId={selectedEvent}
                                    onSaveSearchState={handleSaveSearchState}
                                    initialSearchState={lastManualSearch}
                                    dataSource={dataSource}
                                />
                            )}
                        </div>

                        {message && (
                            <div className={`message ${message.type} ${mode === 'tablet' ? 'tablet-overlay' : ''}`}>
                                {message.text}
                            </div>
                        )}
                    </>
                )}

                {currentRegistration && (
                    <BadgePreview 
                        registration={currentRegistration}
                        onPrintComplete={handlePrintComplete}
                        onCancel={handleCancel}
                        autoPrint={autoPrintMode}
                    />
                )}
            </main>

            {showConfirm && pendingRegistration && (
                <ConfirmDialog 
                    message={`Badge for ${pendingRegistration.name} has already been printed ${pendingRegistration.badgePrinted} time(s). Print again?`}
                    onConfirm={handleConfirmPrint}
                    onCancel={handleCancelPrint}
                />
            )}

            {showEventSelector && pendingRegistrations.length > 1 && (
                <div className="modal-overlay">
                    <div className="modal">
                        <h3>Multiple Registrations Found</h3>
                        <p>This contact is registered for multiple events. Please select which event to print the badge for:</p>
                        <div className="results-list">
                            {pendingRegistrations.map((reg, index) => (
                                <div key={reg.qrCode || reg.id || index} className="result-item">
                                    <div className="result-info">
                                        <h3>{reg.name}</h3>
                                        <p>{reg.eventName || 'Event'}</p>
                                        <p style={{ fontSize: '0.8rem', color: '#999' }}>
                                            {reg.title} at {reg.company}
                                        </p>
                                    </div>
                                    <button 
                                        className="select-btn"
                                        onClick={() => handleSelectRegistrationFromList(reg)}
                                    >
                                        Select
                                    </button>
                                </div>
                            ))}
                        </div>
                        <div style={{ marginTop: '1rem', textAlign: 'center' }}>
                            <button 
                                className="modal-btn secondary"
                                onClick={() => {
                                    setShowEventSelector(false);
                                    setPendingRegistrations([]);
                                    setMessage(null);
                                }}
                            >
                                Back
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showLabelPreview && (
                <div className="label-preview-modal">
                    <div className="label-preview-modal-content">
                        <button 
                            className="modal-close-btn"
                            onClick={() => setShowLabelPreview(false)}
                        >
                            ✕
                        </button>
                        <LabelPreview />
                    </div>
                </div>
            )}
        </div>
    );
}

export default App;
